import { useState, useCallback, useEffect } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Polyline,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import RealTimeInfoWindow from "./RealTimeInfoWindow";
import StopList from "./StopList";
import ScheduleStopList from "./ScheduleStopList";
import TransfersComponent from "./TransfersComponent";
import {
  formatDirectionLabel,
  sortDirections,
} from "../utils/directionLabels";

const containerStyle = {
  width: "100%",
  height: "456px",
};

const defaultCenter = {
  lat: 42.918721, // Grand Rapids coordinates
  lng: -85.627098,
};

const defaultZoom = 12;

const stopIcon = {
  url: "/themes/custom/the_rapid/images/greycircle.png",
  // scaledSize: { width: 32, height: 32 }, // Adjust size as needed
};

const MapContainer = () => {
  const [map, setMap] = useState(null);
  const [routeData, setRouteData] = useState(null);
  const [stopsByDirection, setStopsByDirection] = useState(null);
  const [directions, setDirections] = useState([]);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [selectedStop, setSelectedStop] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [showInteractiveMap, setShowInteractiveMap] = useState(false);
  const [staticMapUrl, setStaticMapUrl] = useState("");
  const [isLoadingStops, setIsLoadingStops] = useState(true);
  const [zoom, setZoom] = useState(defaultZoom);
  const [routeColor, setRouteColor] = useState("#777777");
  const [routeNumber, setRouteNumber] = useState("");
  const [isLoadingRouteData, setIsLoadingRouteData] = useState(false);
  const [blockContent, setBlockContent] = useState(null);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState('');
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [directionLabels, setDirectionLabels] = useState({});
  useEffect(() => {
  }, [routeData]);

  // Function to handle direction change from React component
  const handleDirectionChange = useCallback((direction) => {
    setSelectedDirection(direction);
  }, []);

  // Function to handle day of week change from React component
  const handleDayOfWeekChange = useCallback((newDayOfWeek) => {
    setSelectedDayOfWeek(newDayOfWeek);
  }, []);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey:
      process.env.GOOGLE_MAPS_API_KEY ||
      "AIzaSyAUKX47GFG6Q6s_PNoK6Ad1sUbVsXAoq_U",
  });

  useEffect(() => {
    // Get configuration from Drupal settings
    if (window.drupalSettings && window.drupalSettings.trGoogleMap) {
      const config = window.drupalSettings.trGoogleMap;
      
      // Set zoom from Drupal settings if available
      if (config.zoom) {
        setZoom(parseInt(config.zoom, 10));
      }

      // Fetch route data via AJAX if we have an API endpoint
      if (config.apiEndpoint) {
        setIsLoadingRouteData(true);
        const apiUrl = `${config.apiEndpoint}?_format=json`;
        fetch(apiUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then(data => {
            setRouteData(data.routeData);
            setStaticMapUrl(data.staticMapUrl);
            setRouteColor(data.routeColor || "#777777");
            setRouteNumber(data.routeNumber || "");
            setBlockContent(data.blockContent || null);
            setDirectionLabels(data.directionLabels || {});
            
            // Ensure lat and lng are cast to numbers for the map center
            if (data.mapCenter && data.mapCenter.lat && data.mapCenter.lng) {
              const center = {
                lat: Number(data.mapCenter.lat),
                lng: Number(data.mapCenter.lng)
              };
              setMapCenter(center);
            } else {
              setMapCenter(defaultCenter);
            }
            
            setIsLoadingRouteData(false);
          })
          .catch(error => {
            console.error('Error fetching route data:', error);
            setIsLoadingRouteData(false);
          });
      }
    }
  }, []);

  // Helper to fetch all paginated stops
  async function fetchAllStops(url) {
    let allData = [];
    let nextUrl = url;
    while (nextUrl) {
      const res = await fetch(nextUrl);
      const data = await res.json();
      allData = allData.concat(data.data);
      nextUrl = data.links && data.links.next ? data.links.next.href : null;
    }
    return allData;
  }

  // Fetch stops and group by direction
  useEffect(() => {
    if (routeNumber) {
      setIsLoadingStops(true);
      const url = `/jsonapi/transit_entity/route_stop?filter[field_route_stop_route_number]=${routeNumber}&include=field_route_stop_bus_stop`;
      fetchAllStops(url)
        .then((allData) => {
          const stopsWithGeo = (allData || [])
            .map((stop) => {
              const geo = stop.attributes.field_route_stop_geolocation;
              const direction = (
                stop.attributes.field_route_stop_specific_dir || "Unknown"
              ).trim();
              if (geo && geo.lat && geo.lng) {
                return {
                  id: stop.id,
                  lat: geo.lat,
                  lng: geo.lng,
                  title: stop.attributes.field_bus_stop_label || "",
                  stopId: stop.attributes.field_route_stop_stop_id,
                  sortOrder: stop.attributes.field_route_stop_sort_order,
                  direction,
                };
              }
              return null;
            })
            .filter(Boolean);
          // Group by direction
          const grouped = {};
          stopsWithGeo.forEach((stop) => {
            if (!grouped[stop.direction]) grouped[stop.direction] = [];
            
            grouped[stop.direction][stop.sortOrder] = stop;
          });
          const directionKeys = sortDirections(Object.keys(grouped));
          setStopsByDirection(grouped);
          setDirections(directionKeys);
          setSelectedDirection(
            directionKeys.length > 0 ? directionKeys[0] : null
          );
          setIsLoadingStops(false);
        })
        .catch((error) => {
          console.error("Error fetching stops:", error);
          setIsLoadingStops(false);
        });
    } else {
      setIsLoadingStops(false);
    }
  }, [routeNumber, directionLabels]);



  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback() {
    setMap(null);
  }, []);

  // Fetch real-time data for a stop
  const fetchRealtimeData = (stopId, stopTitle, routeId = null) => {
    if (!stopId) return;
    setRealtimeData(null); // Reset while loading
    let url = `/api/bus-realtime/${encodeURIComponent(stopId)}`;
    if (routeId) {
      url += `?route_id=${encodeURIComponent(routeId)}`;
    }
    url += (url.includes("?") ? "&" : "?") + "_format=json";
    fetch(url, { cache: "no-store" })
      .then((res) => res.json())
      .then((data) => setRealtimeData(data))
      .catch(() =>
        setRealtimeData({ error: "Unable to fetch real-time data." })
      );
  };

  // Fit map to bounds if we have route data
  useEffect(() => {
    if (
      map &&
      routeData &&
      routeData.bounds &&
      routeData.paths &&
      routeData.paths.length > 0
    ) {
      const bounds = new window.google.maps.LatLngBounds();
      routeData.paths.forEach((segment) => {
        segment.forEach((point) => bounds.extend(point));
      });
      // map.fitBounds(bounds);
    }
  }, [map, routeData]);

  // Use route color from state (set by AJAX response)
  // Fallback to KML style if available
  const finalRouteColor = routeColor || 
    (routeData && routeData.style && routeData.style.color) || 
    "#777777";

  // Get stops for the selected direction
  const stops = isLoadingStops
    ? null
    : stopsByDirection?.[selectedDirection] || [];

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  const handleMapClick = () => {
    setShowInteractiveMap(true);
  };

  const handleStopClick = (stop) => {
    setShowInteractiveMap(true);
    setSelectedStop(stop);
    fetchRealtimeData(stop.stopId, stop.title, routeNumber);
    
    // Center the map on the clicked marker
    if (map) {
      map.panTo({ lat: stop.lat, lng: stop.lng });
    }
  };

  const mapHeight = window.innerWidth < 992 ? 220 : 465;

  return (
    <div className="container map-section-wrapper">
      <div className="row">
        <div className="col-12 py-5">
          <h4 className="mb-5 mb-lg-0 maps-stops-heading">Maps & Stops</h4>
          <div className="d-flex flex-lg-row flex-column justify-content-between maps-stops-details">
            <section className="d-flex align-items-lg-end">
              <h6 className="mb-5 mb-lg-0">
                Select a stop to see real-time information.
              </h6>
            </section>
            <section className="d-flex flex-column align-items-lg-end ">
              <h6>Travel Direction</h6>
              <div className="direction-selector d-flex gap-2">
                {directions.map((dir) => (
                  <button
                    key={dir}
                    data-direction={dir}
                    className={`btn d-flex gap-2 ${
                      selectedDirection === dir
                        ? "btn-primary__small"
                        : "btn-primary__reversed__small flex-row-reverse"
                    }`}
                    onClick={() => handleDirectionChange(dir)}
                  >
                    <span className={`icon-arrow-left icon-arrow-${dir.toLowerCase()}`}></span>
                    <span className="eyebrow">{formatDirectionLabel(dir, directionLabels)}</span>
                  </button>
                ))}
              </div>
            </section>
          </div>
        </div>
        <div className="map-container col-12 col-lg-9 pe-lg-4">
          {isLoadingRouteData ? (
            <div
              style={{
                width: '100%',
                height: `${mapHeight}px`,
                background: '#eee',
                borderRadius: '8px',
                border: '2px solid #B4BBC5',
              }}
            />
          ) : !showInteractiveMap && staticMapUrl ? (
            <div
              className="static-map-container"
              onClick={handleMapClick}
              style={{
                width: "100%",
                height: `${mapHeight}px`,
                cursor: "pointer",
                position: "relative",
                borderRadius: "8px",
                border: "2px solid #B4BBC5",
                overflow: "hidden", // Add this to ensure image fits properly
              }}
            >
              <img
                src={staticMapUrl}
                alt="Route map"
                fetchPriority="high"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "10px",
                  right: "10px",
                  background: "rgba(255, 255, 255, 0.9)",
                  padding: "5px 10px",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                Click to view interactive map
              </div>
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={mapCenter}
              zoom={zoom}
              onLoad={onLoad}
              onUnmount={onUnmount}
              style={{ borderRadius: "8px", border: "2px solid #B4BBC5" }}
            >
              {/* Render route polylines */}
              {routeData &&
                routeData.paths &&
                routeData.paths.length > 0 &&
                routeData.paths.map((segment, idx) => (
                  <Polyline
                    key={idx}
                    path={segment}
                    options={{
                      strokeColor: finalRouteColor,
                      strokeOpacity: 1.0,
                      strokeWeight: 6,
                    }}
                  />
                ))}
              {/* Render stop markers */}
              {(stops || []).map((stop) => (
                <Marker
                  key={stop.id}
                  position={{ lat: stop.lat, lng: stop.lng }}
                  title={stop.title}
                  icon={stopIcon}
                  onClick={() => handleStopClick(stop)}
                />
              ))}
              {/* InfoWindow for real-time data */}
              {selectedStop && (
                <InfoWindow
                  position={{ lat: selectedStop.lat, lng: selectedStop.lng }}
                  onCloseClick={() => {
                    setSelectedStop(null);
                    setRealtimeData(null);
                  }}
                >
                  <div style={{ minWidth: 200 }}>
                    {realtimeData === null && (
                      <div>Loading real-time data...</div>
                    )}
                    {realtimeData && realtimeData.error && (
                      <div className="text-warning">{realtimeData.error}</div>
                    )}
                    {realtimeData &&
                      !realtimeData.error &&
                      Array.isArray(realtimeData) && (
                        <RealTimeInfoWindow
                          data={realtimeData}
                          stopTitle={selectedStop.title}
                        />
                      )}
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          )}
          
          {/* Download Map & Schedule Button */}
          {blockContent && blockContent.mapImage && (
            <div className="download-section mt-3">
              <a 
                href={blockContent.mapImage.url} 
                download={blockContent.mapImage.filename}
                className="btn btn-secondary btn-map-download mb-5"
                style={{ width: 'fit-content' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                </svg>
                Download Map & Schedule
              </a>
            </div>
          )}
        </div>

        <div className="col-12 col-lg-3 ps-lg-4">
          <StopList
            stops={stops}
            onStopClick={handleStopClick}
            selectedStopId={selectedStop ? selectedStop.id : null}
          />
        </div>
      </div>
      
      {/* Block Content Section */}
      {blockContent && (
        <div className="row">
          <div className="col-12">
            {/* Map Instructions Block */}
            {blockContent.mapInstructions && (
              <div className="map-instructions-block mb-4 mb-lg-8">
                <div 
                  className="block-content"
                  dangerouslySetInnerHTML={{ __html: blockContent.mapInstructions }}
                />
              </div>
            )}
            
            {/* Route Notification Block */}
            {blockContent.routeNotification && (
              <div className="route-notification-block mb-4">
                <div 
                  className="block-content"
                  dangerouslySetInnerHTML={{ __html: blockContent.routeNotification }}
                />
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Schedules and Transfers Section */}
      {routeData && routeData.routeId ? (
        <div className="row">
          <div className="col-12">
            <ScheduleStopList
              routeId={routeData.routeId}
              direction={selectedDirection}
              dayOfWeek={selectedDayOfWeek}
              onDirectionChange={handleDirectionChange}
              onDayOfWeekChange={handleDayOfWeekChange}
              directionLabels={directionLabels}
            />
          </div>
          <div className="col-12">
            {blockContent && blockContent.routeTransfers && (
              <TransfersComponent transfersHTML={blockContent.routeTransfers} />
            )}
          </div>
        </div>
      ) : (
        <div className="row mt-4">
          <div className="col-12">
            <div style={{ border: '2px solid blue', padding: '10px', margin: '10px 0' }}>
              <p>Debug: ScheduleStopList not rendering because:</p>
              <p>routeData exists: {routeData ? 'Yes' : 'No'}</p>
              <p>routeData.routeId exists: {routeData && routeData.routeId ? 'Yes' : 'No'}</p>
              <p>routeData content: {JSON.stringify(routeData, null, 2)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapContainer;
